import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_CONFIG from '../config/api';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import DateRangePicker from '../components/DateRangePicker';
import {
  CurrencyEuroIcon,
  ChartBarIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentChartBarIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import Select from 'react-select';

// Registrazione componenti Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StatisticheFatturatoPage = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    dataInizio: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Inizio anno
    dataFine: new Date().toISOString().split('T')[0] // Oggi
  });
  
  // Stati per i dati statistici
  const [statisticheFatturato, setStatisticheFatturato] = useState({
    riepilogoGenerale: {
      contrattiAttivi: 0,
      fatturatoTotale: 0,
      fatturatoMedio: 0,
      crescitaMensile: 0
    },
    fatturatoPerMese: [],
    fatturatoPerAgenzia: [],
    topVetture: [],
    previsioniProssimi3Mesi: []
  });

  const [filtri, setFiltri] = useState({
    agenzia: '',
    tipoContratto: '',
    statoContratto: 'attivo'
  });

  const [filtriVisibili, setFiltriVisibili] = useState(false);
  const [agenzieDisponibili, setAgenzieDisponibili] = useState([]);
  const [nodiDisponibili, setNodiDisponibili] = useState([]);

  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  const isAgenzia = ruoloUtente === 'agenzia';
  const isSubagente = ruoloUtente === 'subagente' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, allNodes, canViewNode } = useNetworkHierarchy(user);

  // Imposta filtri di default basati sulla gerarchia
  useEffect(() => {
    if (!isAdmin && userNode) {
      // Per utenti non admin, imposta automaticamente il filtro sul loro nodo
      setFiltri(prev => ({
        ...prev,
        agenzia: userNode.id.toString()
      }));
    }
  }, [isAdmin, userNode]);

  // Carica lista agenzie per il filtro (filtrate per gerarchia)
  const caricaAgenzie = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.baseURL}/api/utenti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data && response.data.dati && response.data.dati.utenti) {
        let utentiFiltrati = response.data.dati.utenti.filter(user => 
          user.role === 'admin' || user.role === 'agenzia' || user.role === 'subagente' || user.role === 'team_leader' || user.role === 'segnalatore'
        );
        
        // Filtra gli utenti in base alla gerarchia
        if (!isAdmin && subordinateNodes.length > 0) {
          // L'utente può vedere solo se stesso e gli utenti dei nodi subordinati
          const utentiVisibili = [user.id, ...subordinateNodes.map(n => n.utenteId).filter(Boolean)];
          utentiFiltrati = utentiFiltrati.filter(utente => utentiVisibili.includes(utente.id));
        }
        
        setAgenzieDisponibili(utentiFiltrati);
      }
    } catch (error) {
      console.error('Errore nel caricamento agenzie:', error);
    }
  };

  // Carica lista nodi per il filtro (filtrati per gerarchia)
  const caricaNodiRete = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.baseURL}/api/network/rete-corrente`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data && response.data.data && response.data.data.nodi) {
        let nodiAttivi = response.data.data.nodi.filter(nodo => nodo.attivo);
        
        // Filtra i nodi in base alla gerarchia dell'utente
        if (!isAdmin && userNode) {
          // L'utente può vedere solo il suo nodo e quelli subordinati
          const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
          nodiAttivi = nodiAttivi.filter(nodo => nodiVisibili.includes(nodo.id));
        }
        
        setNodiDisponibili(nodiAttivi);
      } else {
        setNodiDisponibili([]);
      }
    } catch (error) {
      setNodiDisponibili([]);
    }
  };

  // Carica le statistiche fatturato
  const caricaStatisticheFatturato = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_CONFIG.baseURL}/api/statistiche/fatturato`, {
        params: {
          dataInizio: dateRange.dataInizio,
          dataFine: dateRange.dataFine,
          ...filtri
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data && response.data.success) {
        let statistiche = response.data.statistiche;
        
        // Filtra i dati delle statistiche in base alla gerarchia dell'utente
        if (!isAdmin && userNode && subordinateNodes.length >= 0) {
          const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
          const utentiVisibili = [user.id, ...subordinateNodes.map(n => n.utenteId).filter(Boolean)];
          
          // Filtra il fatturato per agenzia - mostra solo agenzie visibili
          if (statistiche.fatturatoPerAgenzia) {
            statistiche.fatturatoPerAgenzia = statistiche.fatturatoPerAgenzia.filter(agenzia => {
              // Controlla se l'agenzia è nell'elenco degli utenti visibili
              // Questo è un controllo di sicurezza aggiuntivo
              return true; // Il backend dovrebbe già filtrare, ma manteniamo per sicurezza
            });
          }
        }
        
        setStatisticheFatturato(statistiche);
      } else {
        throw new Error('Formato dati non valido');
      }
      
    } catch (error) {
      console.error('Errore nel caricamento statistiche fatturato:', error);
      toast.error('Errore nel caricamento delle statistiche fatturato');
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subordinateNodes.length >= 0) { // Aspetta che i dati siano caricati
      caricaAgenzie();
    }
  }, [subordinateNodes, isAdmin]);

  useEffect(() => {
    if (subordinateNodes.length >= 0) { // Aspetta che i dati siano caricati
      caricaNodiRete();
    }
  }, [subordinateNodes, isAdmin, userNode]);

  useEffect(() => {
    caricaStatisticheFatturato();
  }, [dateRange, filtri]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Funzione per costruire la gerarchia dei nodi sotto ogni agenzia primaria
  function costruisciAlberoNodi(nodi, parentId = null, livello = 0) {
    return nodi
      .filter(n => n.parentId === parentId)
      .sort((a, b) => (a.tipo.localeCompare(b.tipo)) || a.nome.localeCompare(b.nome))
      .flatMap(n => [
        { ...n, livello },
        ...costruisciAlberoNodi(nodi, n.id, livello + 1)
      ]);
  }
  function raggruppaNodiPerAgenziaPrimaria(nodi) {
    // Filtra i nodi in base alla gerarchia dell'utente
    let nodiFiltrati = nodi;
    if (!isAdmin && userNode) {
      const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
      nodiFiltrati = nodi.filter(nodo => nodiVisibili.includes(nodo.id));
    }
    
    const agenzie = nodiFiltrati.filter(n => n.tipo === 'agenzia_primaria');
    return agenzie.map(agenzia => {
      const figli = costruisciAlberoNodi(nodiFiltrati, agenzia.id, 1);
      return {
        label: agenzia.nome + (agenzia.utente ? ` (${agenzia.utente.nome} ${agenzia.utente.cognome})` : ''),
        options: [
          {
            value: agenzia.id,
            label: agenzia.nome + (agenzia.utente ? ` (${agenzia.utente.nome} ${agenzia.utente.cognome})` : ''),
            tipo: agenzia.tipo,
            livello: 0,
            nodo: agenzia
          },
          ...figli.map(nodo => ({
            value: nodo.id,
            label: `${'— '.repeat(nodo.livello)}${nodo.nome}${nodo.utente ? ` (${nodo.utente.nome} ${nodo.utente.cognome})` : ''}`,
            tipo: nodo.tipo,
            livello: nodo.livello,
            nodo
          }))
        ]
      };
    });
  }

  // Configurazione grafico fatturato mensile
  const chartFatturatoMensile = {
    labels: statisticheFatturato.fatturatoPerMese.map(item => item.mese),
    datasets: [
      {
        label: 'Fatturato €',
        data: statisticheFatturato.fatturatoPerMese.map(item => item.fatturato),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'Contratti',
        data: statisticheFatturato.fatturatoPerMese.map(item => item.contratti * 1000), // Scala per visualizzare bene
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
        tension: 0.4,
        yAxisID: 'y1',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Andamento Fatturato Mensile'
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function(value) {
            return (value / 1000).toFixed(0);
          }
        }
      },
    },
  };

  // Configurazione grafico agenzie (Doughnut)
  const chartAgenzie = {
    labels: statisticheFatturato.fatturatoPerAgenzia.map(item => item.nomeAgenzia),
    datasets: [
      {
        data: statisticheFatturato.fatturatoPerAgenzia.map(item => item.fatturato),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)'
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)'
        ],
        borderWidth: 2,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Fatturato per Agenzia'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
  };

  const resetFiltri = () => {
    setFiltri({
      agenzia: '',
      tipoContratto: '',
      statoContratto: 'attivo'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-start gap-3 sm:gap-4 mb-4">
          <div className="p-2 sm:p-3 bg-blue-100 rounded-lg sm:rounded-xl flex-shrink-0">
            <ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Statistiche Fatturato</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              {isAdmin 
                ? 'Analisi dettagliata del fatturato e dei contratti attivi di tutto il sistema'
                : `Analisi del tuo fatturato ${userNode ? `(${userNode.nome})` : ''} e dei nodi subordinati`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Filtri periodo e filtri aggiuntivi */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
            <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtri</h3>
          <button
            onClick={() => setFiltriVisibili(!filtriVisibili)}
            className="ml-auto lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            {filtriVisibili ? (
              <XMarkIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        <div className={`space-y-4 ${filtriVisibili ? 'block' : 'hidden lg:block'}`}>
          {/* Periodo */}
          <DateRangePicker
            startDate={dateRange.dataInizio}
            endDate={dateRange.dataFine}
            onDatesChange={(dates) => setDateRange({ dataInizio: dates.startDate, dataFine: dates.endDate })}
          />

          {/* Filtri aggiuntivi */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nodo rete
              </label>
              <Select
                classNamePrefix="select-nodo-rete"
                options={raggruppaNodiPerAgenziaPrimaria(nodiDisponibili)}
                value={raggruppaNodiPerAgenziaPrimaria(nodiDisponibili).flatMap(g => g.options).find(opt => opt.value === filtri.agenzia) || null}
                onChange={opt => setFiltri(prev => ({ ...prev, agenzia: opt ? opt.value : '' }))}
                placeholder="Seleziona nodo della rete..."
                isClearable
                isSearchable
                formatOptionLabel={(option, { context }) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: option.livello * 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>
                      {option.tipo === 'agenzia_primaria' && '🏢'}
                      {option.tipo === 'team_leader' && '👔'}
                      {option.tipo === 'agente' && '🧑‍💼'}
                      {option.tipo === 'segnalatore' && '📣'}
                      {option.tipo === 'sito_vetrina' && '🌐'}
                    </span>
                    <span>{option.label}</span>
                    {context === 'menu' && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#aaa' }}>{option.tipo.replace('_', ' ')}</span>
                    )}
                  </div>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo Contratto
              </label>
              <select
                value={filtri.tipoContratto}
                onChange={(e) => setFiltri(prev => ({ ...prev, tipoContratto: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti i tipi</option>
                <option value="noleggio_lungo_termine">Noleggio Lungo Termine</option>
                <option value="leasing">Leasing</option>
                <option value="finanziamento">Finanziamento</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stato Contratto
              </label>
              <select
                value={filtri.statoContratto}
                onChange={(e) => setFiltri(prev => ({ ...prev, statoContratto: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti gli stati</option>
                <option value="attivo">Attivo</option>
                <option value="in_attesa">In Attesa</option>
                <option value="sospeso">Sospeso</option>
                <option value="terminato">Terminato</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={resetFiltri}
              className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-6 rounded-lg transition-colors"
            >
              Reset Filtri
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 text-sm sm:text-base">Caricamento statistiche...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {/* Cards riepilogo responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-blue-100 rounded-lg sm:rounded-xl flex-shrink-0">
                  <DocumentChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">Contratti Attivi</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                    {statisticheFatturato.riepilogoGenerale.contrattiAttivi?.toLocaleString('it-IT') || '0'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg sm:rounded-xl flex-shrink-0">
                  <CurrencyEuroIcon className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600" />
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">Fatturato Totale</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                    {formatCurrency(statisticheFatturato.riepilogoGenerale.fatturatoTotale || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center">
                <div className="p-2 sm:p-3 bg-purple-100 rounded-lg sm:rounded-xl flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">Fatturato Medio</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                    {formatCurrency(statisticheFatturato.riepilogoGenerale.fatturatoMedio || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center">
                <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0 ${
                  (statisticheFatturato.riepilogoGenerale.crescitaMensile || 0) >= 0 
                    ? 'bg-green-100' 
                    : 'bg-red-100'
                }`}>
                  {(statisticheFatturato.riepilogoGenerale.crescitaMensile || 0) >= 0 ? (
                    <ArrowTrendingUpIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
                  )}
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">Crescita Mensile</p>
                  <p className={`text-xl sm:text-2xl font-bold truncate ${
                    (statisticheFatturato.riepilogoGenerale.crescitaMensile || 0) >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatPercentage(statisticheFatturato.riepilogoGenerale.crescitaMensile || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Grafici responsive */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
            {/* Grafico andamento mensile */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                Andamento Fatturato Mensile
              </h3>
              <div className="h-64 sm:h-80">
                {statisticheFatturato.fatturatoPerMese.length > 0 ? (
                  <Bar 
                    data={chartFatturatoMensile} 
                    options={{
                      ...chartOptions,
                      maintainAspectRatio: false,
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          position: 'top',
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: {
                              size: window.innerWidth < 640 ? 12 : 14
                            }
                          }
                        }
                      }
                    }} 
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <ChartBarIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-sm sm:text-base">Nessun dato disponibile per il periodo selezionato</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top agenzie/subordinati */}
            {(isAdmin || statisticheFatturato.fatturatoPerAgenzia.length > 0) && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                  {isAdmin ? 'Top 5 Agenzie per Fatturato' : 'Fatturato Rete'}
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {statisticheFatturato.fatturatoPerAgenzia.length > 0 ? (
                    statisticheFatturato.fatturatoPerAgenzia.slice(0, 5).map((agenzia, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0 ${
                            index === 0 ? 'bg-yellow-500' : 
                            index === 1 ? 'bg-gray-400' : 
                            index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {agenzia.nomeAgenzia || `${agenzia.agenzia?.nome || ''} ${agenzia.agenzia?.cognome || ''}`.trim() || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {agenzia.contratti} contratti
                            </p>
                          </div>
                        </div>
                        <div className="text-right ml-3 flex-shrink-0">
                          <p className="font-bold text-gray-900 text-sm sm:text-base">
                            {formatCurrency(agenzia.fatturato)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <BuildingOfficeIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-sm sm:text-base">Nessuna agenzia trovata</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tabella top vetture responsive */}
          {statisticheFatturato.topVetture && statisticheFatturato.topVetture.length > 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Top 10 Vetture per Fatturato
                </h3>
              </div>

              {/* Vista mobile - Cards */}
              <div className="block lg:hidden divide-y divide-gray-200">
                {statisticheFatturato.topVetture.slice(0, 10).map((vettura, index) => (
                  <div key={index} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold text-white mr-3 ${
                          index < 3 ? 'bg-yellow-500' : 'bg-gray-400'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <h4 className="font-medium text-gray-900">{vettura.brand} {vettura.modello}</h4>
                          <p className="text-sm text-gray-500">{vettura.contratti} contratti</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{formatCurrency(vettura.fatturato)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista desktop - Tabella */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Posizione
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vettura
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contratti
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fatturato
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statisticheFatturato.topVetture.slice(0, 10).map((vettura, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold text-white ${
                            index === 0 ? 'bg-yellow-500' : 
                            index === 1 ? 'bg-gray-400' : 
                            index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                          }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {vettura.brand} {vettura.modello}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-900">
                            {vettura.contratti}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(vettura.fatturato)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatisticheFatturatoPage; 